type ParsedHexColor = {
  rgb: string
  alpha: string | null
  format: 'rgb' | 'argb'
}

const HEX_RGB = /^[0-9a-fA-F]{6}$/
const HEX_ARGB = /^[0-9a-fA-F]{8}$/
const HEX_RGBA = /^[0-9a-fA-F]{8}$/

const normalizeHex = (value: string) => value.toUpperCase()

export const parseHexColor = (value?: string | null): ParsedHexColor | null => {
  if (!value) return null
  const trimmed = value.trim()
  if (trimmed.length === 0) return null
  const hex = trimmed.startsWith('#') ? trimmed.slice(1) : trimmed
  if (HEX_ARGB.test(hex)) {
    const normalized = normalizeHex(hex)
    return {
      alpha: normalized.slice(0, 2),
      rgb: normalized.slice(2),
      format: 'argb',
    }
  }
  if (HEX_RGB.test(hex)) {
    const normalized = normalizeHex(hex)
    return {
      alpha: null,
      rgb: normalized,
      format: 'rgb',
    }
  }
  return null
}

export const toCssColor = (value?: string | null): string | null => {
  const parsed = parseHexColor(value)
  if (!parsed) return null
  const { rgb, alpha } = parsed
  if (!alpha) return `#${rgb}`
  const r = Number.parseInt(rgb.slice(0, 2), 16)
  const g = Number.parseInt(rgb.slice(2, 4), 16)
  const b = Number.parseInt(rgb.slice(4, 6), 16)
  const a = Number.parseInt(alpha, 16) / 255
  const clamped = Number.isNaN(a) ? 1 : Math.max(0, Math.min(1, a))
  return `rgba(${r}, ${g}, ${b}, ${Number(clamped.toFixed(3))})`
}

export const toHexRgba = (value?: string | null): string => {
  const parsed = parseHexColor(value)
  if (!parsed) return '#000000FF'
  const alpha = parsed.alpha ?? 'FF'
  return `#${parsed.rgb}${alpha}`
}

export const fromHexRgba = (value?: string | null): string | null => {
  if (!value) return null
  const trimmed = value.trim()
  if (trimmed.length === 0) return null
  const hex = trimmed.startsWith('#') ? trimmed.slice(1) : trimmed
  if (HEX_RGBA.test(hex)) {
    const normalized = normalizeHex(hex)
    const rgb = normalized.slice(0, 6)
    const alpha = normalized.slice(6, 8)
    return `#${alpha}${rgb}`
  }
  if (HEX_RGB.test(hex)) {
    return `#${normalizeHex(hex)}`
  }
  return null
}
